import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { HomeTabs } from '@local/client-contracts';
import { PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { EventInfoSearch, EventsService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { KeyName } from '@local/ts-infra';
import moment, { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { WorkspacesService } from 'src/app/bar/services';
import { CalendarService } from 'src/app/bar/services/calendar.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { TelemetryHelperService, ViewPages } from 'src/app/bar/services/telemetry.helper.service';
import { CalendarEvent, ResultItem } from '../../../results/models/results-types';
import { CalendarWidgetItemComponent } from '../../components/calendar-widget-item/calendar-widget-item.component';
import { WidgetsService } from '../../services/widgets.service';
import { WidgetBaseComponent } from '../widget-base.component';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';

@UntilDestroy()
@Component({
  selector: 'calendar-widget',
  templateUrl: './calendar-widget.component.html',
  styleUrls: ['./calendar-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarWidgetComponent extends WidgetBaseComponent<HomeTabs.Widget> implements OnInit, OnDestroy {
  @ViewChildren('resultItem') resultItemsComponents: QueryList<CalendarWidgetItemComponent>;
  @ViewChild('calendarTitle') calendarTitle: ElementRef;

  pageNameForTelemetry: ViewPages;
  amountOfCurrentItems?: number;
  error: any;
  searchInfo: Partial<EventInfoSearch>;
  widgetRowHeight = 65;
  soonEvent: CalendarEvent;
  noConnectApps = false;
  isToday: boolean;

  private _today: Moment;
  private _currentDate: moment.Moment;
  private updateTodayTimeout: any;
  private currentSubscription: Subscription;
  static readonly MIN_WIDGET_SIZE: number = 365;
  readonly minHeight: number = 340;
  readonly WIDGET_HEADER = 65;

  get currentDate(): moment.Moment {
    return this._currentDate;
  }

  set currentDate(date: moment.Moment) {
    this._currentDate = date;
    this.isToday = this.isCurrentDateToday();
  }

  get today(): moment.Moment {
    return this._today;
  }

  set today(date: moment.Moment) {
    this._today = date;
    this.isToday = this.isCurrentDateToday();
  }

  get disabled(): boolean {
    return (!this.isLoading && !this.displayedContext?.items) || this.noConnectApps;
  }

  get items() {
    return this.displayedContext?.items;
  }

  constructor(
    protected elementRef: ElementRef,
    protected routerService: RouterService,
    private calendarService: CalendarService,
    private barService: HubService,
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    protected ngZone: NgZone,
    protected eventService: EventsService,
    protected session: SessionService,
    private applicationsService: ApplicationsService,
    private telemetryHelperService: TelemetryHelperService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected popupService: PopupService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService
  ) {
    super(
      elementRef,
      cdr,
      keyboardService,
      widgetsService,
      ngZone,
      session,
      contextMenuService,
      workspacesService,
      popupService,
      eventService,
      routerService,
      homeTabsService,
      avatarListService
    );
    this.pageNameForTelemetry = 'calendar';
    this.updateToday();
  }

  updateToday() {
    this.today = moment().startOf('day');
    if (!this.currentDate) this.currentDate = this.today.clone();

    this.updateTodayTimeout = setTimeout(() => {}, this.today.clone().add(1, 'days').diff(moment(Date.now())));
  }

  ngOnInit() {
    super.ngOnInit();
    this.pageType = 'calendar';
    this.isLoading = true;
    this.minWidgetSize = CalendarWidgetComponent.MIN_WIDGET_SIZE;
    this.applicationsService.all$.pipe(untilDestroyed(this)).subscribe(async () => {
      const apps = await this.applicationsService.hasType(['event']);
      if (!apps.length) {
        this.error = 'No Calendar Apps';
        this.isLoading = false;
        this.cdr.markForCheck();
        return;
      }
      if (apps.every((app) => app.links?.length === 0)) {
        this.noConnectApps = true;
        this.isLoading = false;
        this.cdr.markForCheck();
        return;
      }
      this.error = null;
      this.noConnectApps = false;
      this.isLoading = true;
      this.loadData(true);
    });
  }

  loadData(withRtf?: boolean) {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
      this.currentSubscription = null;
    }
    let first = true;
    this.today = moment().startOf('day');
    if (this.currentDate.valueOf() < this.today.valueOf()) {
      this.currentDate = this.today.clone();
    }
    const start = this.isToday ? Date.now() : this.currentDate.valueOf();
    this.currentSubscription = this.calendarService
      .watch$(start, this.currentDate.clone().add(1, 'day').valueOf(), withRtf)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          const events = result.events;
          this.displayedContext.items = events;
          this.amountOfCurrentItems = events.length;
          this.isLoading = first && !result.searchDone && events.length === 0;
          first = false;
          if (this.displayedContext.items.length > 0) {
            this.telemetryHelperService.impressionEvent('calendar', <ResultItem[]>this.displayedContext.items, {});
          }
          if (this.currentDate.isSame(this.today)) this.soonEvent = events?.find((e) => !e.isAllDay);
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.error = err;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  protected get noScroll(): boolean {
    return this.amountOfCurrentItems * this.widgetRowHeight < this.heightWidget;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.updateTodayTimeout);
  }

  async onResizeChange() {
    await this.afterViewInitPromise;
    this.heightWidget -= this.calendarTitle?.nativeElement.offsetHeight || 0;
  }

  onClickNextDay() {
    if (this.disabled) return;
    this.currentDate = this.currentDate.clone().add(1, 'days');
    this.eventService.event('home_page.widget_calender_date_selection', { target: 'forward_arrow' });
    this.updateSelectedIndex(0);
    this.loadData();
    this.cdr.markForCheck();
  }

  onClickToday() {
    if (this.disabled || this.isToday) return;
    this.currentDate = this.today;
    this.eventService.event('home_page.widget_calender_date_selection', { target: 'today' });
    this.loadData();
    this.cdr.markForCheck();
  }

  onClickPrevDay() {
    if (this.disabled || this.isToday) return;
    this.currentDate = this.currentDate.clone().subtract(1, 'days');
    this.eventService.event('home_page.widget_calender_date_selection', { target: 'backward_arrow' });
    this.updateSelectedIndex(0);
    this.loadData();
    this.cdr.markForCheck();
  }

  tryAgain(): void {
    this.loadData();
  }

  goBack(): void {
    this.barService.goBack();
  }

  connect(): void {
    this.routerService.navigate(['calendar']);
  }

  setFocusWidget() {
    super.setFocusWidget();
    const inProgressIndex: number = (this.displayedContext.items as CalendarEvent[])?.findIndex(
      (i: CalendarEvent) => this.calendarService.getEventType(i.start, i.end) === 'In progress'
    );
    if (inProgressIndex !== -1) {
      this.updateSelectedIndex(inProgressIndex);
    }
  }

  handleRowMenuKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (keys[0] === 'ArrowRight') {
      this.onClickNextDay();
    }
    if (keys[0] === 'ArrowLeft') {
      this.onClickPrevDay();
    }
    const selectedItem = this.resultItemsComponents.toArray().find((i) => i.index === this.selectedIndex);
    selectedItem?.handleKeys(keys);
    this.cdr.markForCheck();
  }

  private isCurrentDateToday() {
    return this.currentDate && this.today && this.currentDate.valueOf() === this.today.valueOf();
  }
}
